import { debounce, scrollElementToMiddle } from '../utils';
import { TextInput } from './TextInput';

export interface GetDataOptions {
  query: string;
  url: string;
}

interface SearchInputOptions {
  getData?: (opts: GetDataOptions) => Promise<string>;
  hideResultsOnInit?: boolean;
  onSearched?: () => void;
}

export class SearchInput extends TextInput {
  private MIN_SEARCH_LENGHT = 3;

  private readonly _searchElement: HTMLElement;
  private readonly _searchJQueryElement: JQuery<HTMLElement>;
  private readonly _searchResults: JQuery<HTMLElement>;
  private readonly _isModal: boolean;
  private readonly _options: SearchInputOptions;
  private readonly _url: string;
  private readonly _getDataHandler: (opts: GetDataOptions) => Promise<string>;

  constructor(searchElement: HTMLElement, options: SearchInputOptions) {
    super(searchElement.closest('.input-group'));

    this._options = options;

    const groupElement = searchElement.closest('.input-group');
    this._searchElement = searchElement;
    this._searchJQueryElement = $(searchElement);

    this._searchResults = $(
      document.getElementById(groupElement?.getAttribute('data-results-id'))
    );
    this._url = groupElement?.getAttribute('data-search-url');

    this._isModal = this._searchResults.data('is-modal');

    this._getDataHandler = this._options.getData ?? this.defaultGetData;

    if (this._options.hideResultsOnInit) {
      this.hideResults();
    }

    this.setupHandlers();
  }

  private setupHandlers() {
    this.onChange(debounce((e: Event) => this.handleChange(e), 300));

    if (this._isModal) {
      $(document).on('click', (e) => this.closeModalResultsOnClickOutside(e));
      this.onFocusIn((e) => this.handleFocusIn(e));
    }
  }

  private showResults() {
    this._searchResults.show();
  }

  private hideResults() {
    this._searchResults.hide();
  }

  private handleChange(e: Event) {
    const query = (e.target as HTMLInputElement).value;
    if (!query || query.length < this.MIN_SEARCH_LENGHT) {
      this.hideResults();
      return;
    }

    this.handleSearch(query);
  }

  private async handleSearch(query: string) {
    const { onSearched } = this._options;

    this.setIsLoading(true);

    const result = await this._getDataHandler({ query, url: this._url });
    this._searchResults.html(result);
    this.showResults();

    this.setIsLoading(false);

    if (onSearched) {
      onSearched();
    }
  }

  private handleFocusIn(query: string) {
    scrollElementToMiddle(this._searchElement, { onlyIfBelow: true });

    if (
      this._searchResults.length > 0 &&
      query?.length >= this.MIN_SEARCH_LENGHT
    ) {
      this.showResults();
    }
  }

  private closeModalResultsOnClickOutside(e: JQuery.ClickEvent) {
    const target = e.target as unknown as Element;
    if (
      !this._searchJQueryElement.has(target).length &&
      this._searchElement !== target
    ) {
      this.hideResults();
    }
  }

  private defaultGetData({ query, url }: GetDataOptions) {
    return new Promise<string>((resolve) => {
      $.get(url, { query }, resolve);
    });
  }
}
